/**
 * `dropdown` — centred label chips, `+N` past the visible width (M2 §5.8, §6).
 * The only tier-1 type whose empty `text` is `null` `[cvs§4.5]`.
 */
import {
  findDropdownIdByLabel,
  findDropdownLabel,
  getColumnType,
  type DropdownSettings,
  type DropdownValue,
} from '@elizmonday/shared';
import { Icon, icons } from '@elizmonday/ui';
import { useState } from 'react';
import { CellPopover, PickerRow, PickerSearch, PickerSeparator, useEditorDraft } from '../primitives';
import { openColumnSettings } from '../settings-request';
import { DropdownSettingsDialog } from './SettingsDialog';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';

const shared = getColumnType('dropdown');

/** Measured on monday: the card is 250 × 306 for three options (`editor.json`). */
export const DROPDOWN_PICKER_WIDTH = 250;

export const dropdownColumnUi = defineColumnUi<DropdownValue, DropdownSettings>({
  type: 'dropdown',
  icon: 'columnDropdown',
  gallery: { group: 'essentials', keywords: ['dropdown', 'tags', 'multi select', 'options'] },
  /** P-022: `6054207833` `Store` (`width: null`) renders **140** px in monday. */
  defaultWidth: 140,
  align: 'center',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Cell: ({ value, settings }) => {
    const ids = value?.ids ?? [];
    if (ids.length === 0) return <span className="ez-cell__empty" aria-hidden="true" />;
    return (
      <span className="ez-chips" data-align="center">
        {ids.slice(0, 2).map((id) => (
          <span key={id} className="ez-chip">
            {findDropdownLabel(settings, id) ?? `#${id}`}
          </span>
        ))}
        {ids.length > 2 ? <span className="ez-chip ez-chip--more">{`+${ids.length - 2}`}</span> : null}
      </span>
    );
  },

  /**
   * Re-verified against `discovery/ui-reference/editors/dropdown/editor.json` (round 4) and
   * corrected on four points the round-3 rebuild got wrong:
   *
   *  - the card is **250** wide, not 240 (radius 8, padding 8, white);
   *  - the search field is **always there** — 234 × 35, radius 4, padding `1px 2px 1px 12px`,
   *    14px/400 — even with three options. That is the opposite of the status picker, which
   *    shows its labels bare, and it is why `PICKER_SEARCH_THRESHOLD` must **not** gate this
   *    one: monday's own capture has three options and a search box;
   *  - option rows are **230 × 40 with an 8 px gap** (48 px pitch), radius 4, padding `0 8px`;
   *  - the footer is a **centred 96 × 40 `Edit labels`** button (monday's wording — round 3
   *    said "Edit Options"), then a 1 px `#d0d4e4` divider, then the AI row, which this
   *    product ships disabled with its reason exactly as the status picker does (`P-003`).
   */
  Editor: ({ value, settings, initialInput, onCommit, onCancel, column }) => {
    const deactivated = new Set(settings.deactivated_labels ?? []);
    const all = settings.labels.filter((label) => !deactivated.has(label.id));
    // The search term is chrome, not a draft: typing in it never writes anything (P-282).
    const [query, setQuery] = useState(initialInput ?? '');
    /** **P-282**: the picked set follows the store while nobody has picked, and an untouched
     * dismiss writes nothing at all — see `useEditorDraft`. */
    const editor = useEditorDraft<number[]>(value?.ids ? [...value.ids] : []);
    const picked = editor.draft;
    const options = all.filter(
      (label) =>
        query.trim() === '' || label.name.toLowerCase().includes(query.trim().toLowerCase()),
    );
    const toggle = (id: number) =>
      editor.update((current) =>
        current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
      );
    return (
      <CellPopover
        label="Pick options"
        width={DROPDOWN_PICKER_WIDTH}
        onDismiss={() =>
          editor.dismiss(
            () => onCommit(picked.length === 0 ? null : ({ ids: picked } as DropdownValue)),
            onCancel,
          )
        }
        onCancel={onCancel}
      >
        {/*
          * monday's `multiple-items-box-component dropdown-chosen` row: the picked options as
          * chips *above* the search field, and the field's placeholder is
          * `Create or find labels` — both read off `editors/dropdown/editor.{json,png}`.
          */}
        <div className="ez-chosen-chips" data-testid="dropdown-chosen">
          {picked.map((id) => (
            <span key={id} className="ez-chip" data-testid={`dropdown-chip-${String(id)}`}>
              {all.find((label) => label.id === id)?.name ?? `#${String(id)}`}
              <button
                type="button"
                className="ez-chip__remove"
                data-testid={`chip-remove-${String(id)}`}
                aria-label={`Remove ${all.find((label) => label.id === id)?.name ?? String(id)}`}
                onClick={() => toggle(id)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <PickerSearch value={query} onChange={setQuery} placeholder="Create or find labels" />
        <ul className="ez-option-list" role="listbox" aria-label="Options" aria-multiselectable="true">
          {options.map((label) => (
            <li key={label.id}>
              <button
                type="button"
                role="option"
                aria-selected={picked.includes(label.id)}
                className="ez-option-list__option"
                data-testid={`dropdown-option-${String(label.id)}`}
                onClick={() => toggle(label.id)}
              >
                <span className="ez-option-list__check">{picked.includes(label.id) ? '✓' : ''}</span>
                {label.name}
              </button>
            </li>
          ))}
          {options.length === 0 ? <li className="ez-option-list__empty">No matching option</li> : null}
        </ul>
        <PickerRow
          icon={<Icon icon={icons.edit} size={16} ignoreFocusStyle />}
          label="Edit labels"
          testId="dropdown-edit-options"
          centered
          onClick={() => openColumnSettings(column.id)}
        />
        <PickerSeparator />
        <PickerRow
          icon={<Icon icon={icons.aiSparkle} size={16} ignoreFocusStyle />}
          label="Auto-assign labels"
          disabled
          title="AI features are not part of this product"
          testId="dropdown-auto-assign"
        />
      </CellPopover>
    );
  },

  SettingsDialog: DropdownSettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const ids = trimmed.split(',').map((part) => {
      const id = findDropdownIdByLabel(settings, part.trim());
      if (id === undefined) throw new Error(`"${part.trim()}" is not an option`);
      return id;
    });
    return { ids } as DropdownValue;
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('dropdown'),
});
